import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = {
  access_token: '',
  auth0User: null,
  user: null,
  isAdmin: false,
  adminRole: null,
  canManageAdminTeam: false,
};

export const restoreAuthSession = createAsyncThunk(
  'authenticationSlice/restoreAuthSession',
  async (_, { rejectWithValue }) => {
    try {
      const access_token = localStorage.getItem('access_token');
      if (!access_token) {
        return rejectWithValue('No session token found');
      }

      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URI}/artist/session`, {
        headers: {
          authorization: access_token,
        },
      });

      if (!response?.data?.success) {
        return rejectWithValue('Failed to restore session');
      }

      return {
        access_token,
        auth0User: response.data.data?.auth0User || null,
        isAdmin: Boolean(response.data.data?.isAdmin),
        adminRole: response.data.data?.adminRole || null,
        canManageAdminTeam: Boolean(response.data.data?.canManageAdminTeam),
      };
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status ?? null,
        ...(error.response?.data || { message: error.message }),
      });
    }
  }
);

export const getProfile = createAsyncThunk(
  'authenticationSlice/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      if (localStorage.getItem('auth0-user') && localStorage.getItem('access_token')) {
        let access_token = localStorage.getItem('access_token');
        let auth0User = JSON.parse(localStorage.getItem('auth0-user'));

        let config = {
          method: 'get',
          maxBodyLength: Infinity,
          url: `${process.env.REACT_APP_BACKEND_URI}/artist/${auth0User?.sub}/data`,
          headers: {
            authorization: access_token,
          },
        };

        const response = await axios.request(config);
        if (response?.data?.data?.length) {
          return {
            access_token,
            data: response.data?.data,
            auth0User: auth0User,
          };
        }
        return {
          access_token,
          data: [],
          auth0User,
        };
      }
      return rejectWithValue('No authenticated session found');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to load profile');
    }
  }
);

export const authenticationSlice = createSlice({
  name: 'authenticationSlice',
  initialState,
  reducers: {
    addArtist: (state, action) => {
      const data =
        typeof action.payload.data === 'string'
          ? JSON.parse(action.payload.data)
          : action.payload.data;
      let config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_BACKEND_URI}/artist/add`,
        headers: {
          'Content-Type': 'application/json',
        },
        data,
      };
      axios
        .request(config)
        .then(() => {
          if (action.payload.callback) {
            action.payload.callback();
          }
        })
        .catch(() => {
          if (action.payload.callback) {
            action.payload.callback();
          }
        });
    },
    updateArtist: (state, action) => {
      const config = {
        method: 'patch',
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_BACKEND_URI}/artist/${action.payload.id}/update`,
        headers: {},
        data: action.payload.data,
      };

      axios
        .request(config)
        .then(() => {
          if (action.payload.callback) {
            action.payload.callback();
          }
        })
        .catch(error => {
          if (action.payload.errorCallback) {
            action.payload.errorCallback(error);
          }
        });
    },
    updateArtistImage: (state, action) => {
      if (!action.payload.imageFile) {
        if (action.payload.callback) {
          action.payload.callback();
        }
        return;
      }

      const formData = new FormData();
      formData.append('image', action.payload.imageFile);

      const config = {
        method: 'patch',
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_BACKEND_URI}/artist/${action.payload.id}/update/image`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      };

      axios
        .request(config)
        .then(() => {
          if (action.payload.callback) {
            action.payload.callback();
          }
        })
        .catch(error => {
          if (action.payload.errorCallback) {
            action.payload.errorCallback(error);
          }
        });
    },
    setIsAdmin: (state, action) => {
      state.isAdmin = action.payload;
    },
    setAdminRole: (state, action) => {
      state.adminRole = action.payload || null;
    },
    setCanManageAdminTeam: (state, action) => {
      state.canManageAdminTeam = Boolean(action.payload);
    },
    updateAuth0UserEmail: (state, action) => {
      const { email, sessionToken } = action.payload || {};
      if (sessionToken) {
        state.access_token = sessionToken;
      }
      if (state.auth0User && email) {
        state.auth0User.email = email;
      }
    },
    clearAuthenticationState: state => {
      state.access_token = '';
      state.auth0User = null;
      state.user = null;
      state.isAdmin = false;
      state.adminRole = null;
      state.canManageAdminTeam = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(restoreAuthSession.fulfilled, (state, action) => {
        state.access_token = action?.payload?.access_token || '';
        const nextAuth0User = action?.payload?.auth0User || null;
        // Skip the assignment when nothing changed so the 60s poll / focus sync
        // doesn't produce a new auth0User reference on every tick.
        if (JSON.stringify(state.auth0User) !== JSON.stringify(nextAuth0User)) {
          state.auth0User = nextAuth0User;
        }
        state.isAdmin = Boolean(action?.payload?.isAdmin);
        state.adminRole = action?.payload?.adminRole || null;
        state.canManageAdminTeam = Boolean(action?.payload?.canManageAdminTeam);
      })
      .addCase(getProfile.pending, state => {})
      .addCase(getProfile.fulfilled, (state, action) => {
        if (!action?.payload?.access_token || !action?.payload?.auth0User) {
          return;
        }
        state.access_token = action.payload.access_token;
        state.auth0User = action.payload.auth0User;
        state.user = action.payload.data?.length ? action.payload.data[0] : null;
      })
      .addCase(getProfile.rejected, state => {});
  },
});

export const {
  addArtist,
  updateArtist,
  updateArtistImage,
  setIsAdmin,
  setAdminRole,
  setCanManageAdminTeam,
  updateAuth0UserEmail,
  clearAuthenticationState,
} = authenticationSlice.actions;

export default authenticationSlice.reducer;
